import { Type } from '@angular/core';
import { ProductMenuHeaderComponent } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-header/product-menu-header.component';
import { MenuHeaderTitleImageComponent } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-header/menu-header-title/menu-header-title-image/menu-header-title-image.component';
import { ReportMenu } from './report-menu';
import { MenuSectionHeaderComponent } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-section-header/menu-section-header.component';
import { OrderReviewSectionHeaderComponent } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-section-header/order-review-section-header/order-review-section-header.component';
import { PrintHeaderLayoutType } from '../../enum/shared/print-header-layout-type.enum';
import { ColumnGrouping } from '../section/column-grouping';
import { ColumnUtils } from '../../../utils/column-utils';
import { Section } from '../section/section';
import { SectionRowViewModel } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/section-row-view-models/SectionRowViewModel';
import { ReportNewProductsSection } from '../section/report/report-new-products-section';
import { FONT, FontStyleSheet } from '../font-style-sheets';
import { SectionColumnType } from '../../enum/shared/section-column-type';
import { SectionColumnProductInfoType } from '../../enum/shared/section-column-product-info-type';
import { SectionColumnViewModel } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/section-column-view-models/SectionColumnViewModel';
import { WithinNameCell } from '../../enum/shared/within-name-cell';
import { exists } from '../../../functions/exists';

export class OrderReviewReportMenu extends ReportMenu {
  getThemeClass(): string {
    return 'order-review-theme';
  }

  whatFontsAreNeededToRenderContent(): FontStyleSheet[] {
    return [FONT.Poppins.Bold_700, FONT.Poppins.Medium_500];
  }

  /**
   * All fonts are sourced from the assets folder
   */
  override getAdobeFontFamilyCssUrl(): string | null {
    return null;
  }

  getPageBottomMargin(): string {
    return '0.25in';
  }

  getPageLeftMargin(): string {
    return '0.25in';
  }

  getPageRightMargin(): string {
    return '0.25in';
  }

  getPageTopMargin(): string {
    return '0.25in';
  }

  getPrintHeaderLayoutType(): PrintHeaderLayoutType {
    return this.menuOptions?.printHeaderLayout || PrintHeaderLayoutType.AllPages;
  }

  getHeaderType(): Type<ProductMenuHeaderComponent> {
    return MenuHeaderTitleImageComponent;
  }

  getSectionsHeaderType(): Type<MenuSectionHeaderComponent> {
    return OrderReviewSectionHeaderComponent;
  }

  getShowInlineLabels(): WithinNameCell {
    return WithinNameCell.TitleLine;
  }

  getShowProductSubtitle(): boolean {
    return false;
  }

  getSectionHeaderUnderlined(): boolean {
    return false;
  }

  shouldFetchVariantAssets(): boolean {
    return true;
  }

  getLineModeColumnGroupings(sectionColumnViewModels: SectionColumnViewModel[]): ColumnGrouping[] {
    return ColumnUtils.oneBigGrouping();
  }

  getShouldInflateColumnHeaderForRow(s: Section, col: SectionColumnViewModel, rows: SectionRowViewModel[]): boolean {
    const assetCol = col?.columnType === SectionColumnProductInfoType.Asset;
    const badgeCol = col?.columnType === SectionColumnProductInfoType.Badge;
    if (s instanceof ReportNewProductsSection && (assetCol || badgeCol)) {
      return false;
    }
    return super.getShouldInflateColumnHeaderForRow(s, col, rows);
  }

  getShouldInflateColumnForRow(s: Section, col: SectionColumnViewModel, row: SectionRowViewModel): boolean {
    const brand = col?.columnType === SectionColumnProductInfoType.Brand;
    return !brand || (brand && exists(row?.getBrand()));
  }

  override getColumnOrdering(): Map<SectionColumnType, number> {
    return new Map<SectionColumnType, number>([
      [SectionColumnProductInfoType.ProductTitle, 0],
      [SectionColumnProductInfoType.Brand, 1],
      [SectionColumnProductInfoType.VariantType, 2],
      [SectionColumnProductInfoType.Strain, 3],
      [SectionColumnProductInfoType.StrainType, 4],
      [SectionColumnProductInfoType.Badge, 5],
      ...SectionColumnViewModel.getDefaultAdditionalDetailsColumnOrder(6),
      SectionColumnViewModel.getDefaultProductTagGroupColumnOrdering(7),
      ...SectionColumnViewModel.getDefaultCannabinoidColumnOrdering(8),
      ...SectionColumnViewModel.getDefaultTerpeneColumnOrdering(9),
      [SectionColumnProductInfoType.Stock, 10],
      [SectionColumnProductInfoType.QuantityAndSize, 11],
      [SectionColumnProductInfoType.Quantity, 12],
      [SectionColumnProductInfoType.Size, 13],
      [SectionColumnProductInfoType.GradingResults, 14],
      [SectionColumnProductInfoType.VariantPrice, 15],
      [SectionColumnProductInfoType.VariantSecondaryPrice, 16]
    ]);
  }
}
